/**
 * The tile catalog (BUILD-SPEC.md §3–5, BUILD-SPEC-2 §4/§6). Consolidated
 * 2026-06-02: the 59 individual calculators are now grouped into 10 topic
 * "hubs" (see hub.ts), so the home grid and All
 * Tools index show a handful of calm areas. Each hub reuses the existing
 * calculators' `mount` functions unchanged behind a segmented control. The
 * command palette and home search query SEARCH_ENTRIES (hubs + every sub-tool),
 * so searching "EITC" or "refinance" still jumps straight to that calculator.
 */
import type { HubConfig } from "./hub";
import { defineHub } from "./hub";
import type { TileDefinition } from "./types";
import { takeHomeTile } from "./takeHome";
import { hourlySalaryTile } from "./hourlySalary";
import { federalIncomeTaxTile } from "./federalIncomeTax";
import { selfEmploymentTaxTile } from "./selfEmploymentTax";
import { quarterlyTaxesTile } from "./quarterlyTaxes";
import { freelanceRateTile } from "./freelanceRate";
import { contractVsSalaryTile } from "./contractVsSalary";
import { selfEmployedRetirementTile } from "./selfEmployedRetirement";
import { marginalExplorerTile } from "./marginalExplorer";
import { loanAmortizationTile } from "./loanAmortization";
import { refinanceTile } from "./refinance";
import { autoLoanTile } from "./autoLoan";
import { compoundGrowthTile } from "./compoundGrowth";
import { retirementOptimizerTile } from "./retirementOptimizer";
import { capitalGainsTile } from "./capitalGains";
import { trumpAccountTile } from "./trumpAccount";
import { savingsBondTile } from "./savingsBond";
import { inflationTile } from "./inflation";
import { rmdTile } from "./rmd";
import { taxLossHarvestingTile } from "./taxLossHarvesting";
import { lotPickerTile } from "./lotPicker";
import { childTaxTile } from "./childTax";
import { educationCreditsTile } from "./educationCredits";
import { rothLadderTile } from "./rothLadder";
import { backdoorRothTile } from "./backdoorRoth";
import { iraDeductionTile } from "./iraDeduction";
import { balanceTransferTile } from "./balanceTransfer";
import { giftTaxTile } from "./giftTax";
import { amtScreenerTile } from "./amtScreener";
import { paycheckOptimizerTile } from "./paycheckOptimizer";
import { w4WithholdingTile } from "./w4Withholding";
import { socialSecurityTile } from "./socialSecurity";
import { socialSecurityTaxTile } from "./socialSecurityTax";
import { drawdownTile } from "./drawdown";
import { collegeCostTile } from "./collegeCost";
import { disabilityTile } from "./disability";
import { umbrellaTile } from "./umbrella";
import { estateChecklistTile } from "./estateChecklist";
import { spendingPlanTile } from "./spendingPlan";
import { homeAffordabilityTile } from "./homeAffordability";
import { sinkingFundTile } from "./sinkingFund";
import { rentVsBuyTile } from "./rentVsBuy";
import { pmiEndDateTile } from "./pmiEndDate";
import { homeSaleGainTile } from "./homeSaleGain";
import { retirementCashOutTile } from "./retirementCashOut";
import { cobraVsMarketplaceTile } from "./cobraVsMarketplace";
import { filingJointlyVsSeparatelyTile } from "./filingJointlyVsSeparately";
import { medicareIrmaaTile } from "./medicareIrmaa";
import { medicarePenaltiesTile } from "./medicarePenalties";
import { ssEarningsTestTile } from "./ssEarningsTest";
import { hsaMedicareTimingTile } from "./hsaMedicareTiming";
import { ssSpousalSurvivorTile } from "./ssSpousalSurvivor";
import { ssiEstimateTile } from "./ssiEstimate";
import { medicareSavingsTile } from "./medicareSavings";
import { healthPlanTile } from "./healthPlan";
import { eobCheckerTile } from "./eobChecker";
import { cashFlowTile } from "./cashFlow";
import { lifeInsuranceTile } from "./lifeInsurance";
import { fplTile } from "./fpl";
import { eitcTile } from "./eitc";
import { childTaxCreditTile } from "./childTaxCredit";
import { owedScreenerTile } from "./owedScreener";
import { acaPtcTile } from "./acaPtc";
import { saversCreditTile } from "./saversCredit";
import { dependentCareTile } from "./dependentCare";
import { studentLoanPaymentTile } from "./studentLoanPayment";
import { secondIncomeTile } from "./secondIncome";
import { snapTile } from "./snap";
import { medicaidTile } from "./medicaid";
import { fafsaSaiTile } from "./fafsaSai";
import { pellTile } from "./pell";
import { peaceOfMindTile } from "./peaceOfMind";
import { freedomDateTile } from "./freedomDate";
import { debtFreedomTile } from "./debtFreedom";
import { downshiftTile } from "./downshift";
import { sabbaticalTile } from "./sabbatical";
import { cliffExplorerTile, marginalRealityTile } from "./benefitCliffs";
import { billTriageTile } from "./billTriage";
import { freeFilingTile } from "./freeFiling";
import { charityCareTile } from "./charityCare";
import { garnishmentTile } from "./garnishment";
import { uiEstimatorTile } from "./uiEstimator";
import { enrollmentWindowsTile } from "./enrollmentWindows";
import { lifeEventsTile } from "./lifeEvents";

// The 10 topic hubs, ordered by pillar so the home grid preserves the
// on-screen order. Each hub's first tool is its default (the one a bare hub
// link opens); the plan engine deep-links rely on those defaults (retirement →
// retirement-optimizer, debt → debt-freedom, where-you-stand → peace-of-mind)
// and on `?tool=` for the rest.
const HUB_CONFIGS: HubConfig[] = [
  {
    id: "paycheck-taxes",
    title: "Paycheck & Taxes",
    pillar: "paycheck",
    description: "Your real take-home pay, withholding, and what you owe, across every state.",
    tools: [
      takeHomeTile,
      w4WithholdingTile,
      hourlySalaryTile,
      federalIncomeTaxTile,
      filingJointlyVsSeparatelyTile,
      marginalExplorerTile,
      paycheckOptimizerTile,
      amtScreenerTile,
      giftTaxTile,
    ],
  },
  {
    id: "self-employed",
    title: "Self-Employed & 1099",
    pillar: "paycheck",
    description:
      "Self-employment tax, quarterly estimates, freelance rates, and the W-2 vs 1099 call.",
    tools: [
      selfEmploymentTaxTile,
      quarterlyTaxesTile,
      freelanceRateTile,
      contractVsSalaryTile,
      selfEmployedRetirementTile,
    ],
  },
  {
    id: "investing",
    title: "Investing",
    pillar: "investing",
    description:
      "Capital gains, cost basis, tax-loss harvesting, growth, and the dollar over time.",
    tools: [
      capitalGainsTile,
      lotPickerTile,
      taxLossHarvestingTile,
      childTaxTile,
      compoundGrowthTile,
      trumpAccountTile,
      savingsBondTile,
      inflationTile,
    ],
  },
  {
    id: "retirement",
    title: "Retirement",
    pillar: "retirement",
    description: "Contributions and the match, Roth moves, Social Security, RMDs, and drawdown.",
    tools: [
      retirementOptimizerTile,
      rothLadderTile,
      backdoorRothTile,
      iraDeductionTile,
      rmdTile,
      drawdownTile,
      retirementCashOutTile,
      socialSecurityTile,
      socialSecurityTaxTile,
      downshiftTile,
    ],
  },
  {
    // SPEC-5 §7.1: the thirteenth hub, and the ceiling. The household at or
    // near 65: Medicare's costs, penalties and clocks, and Social Security
    // for a couple and for someone still working.
    id: "medicare-later-life",
    title: "Medicare & Later Life",
    pillar: "retirement",
    description:
      "Medicare premiums and surcharges at 65, and the Social Security rules around them.",
    tools: [
      medicareIrmaaTile,
      medicarePenaltiesTile,
      hsaMedicareTimingTile,
      ssEarningsTestTile,
      ssSpousalSurvivorTile,
      ssiEstimateTile,
      medicareSavingsTile,
    ],
  },

  {
    id: "debt",
    title: "Borrowing & Debt",
    pillar: "debt",
    description: "Loans, mortgages, refinancing, payoff order, and a clear debt-free date.",
    tools: [
      debtFreedomTile,
      loanAmortizationTile,
      refinanceTile,
      autoLoanTile,
      balanceTransferTile,
      freedomDateTile,
      studentLoanPaymentTile,
    ],
  },
  {
    id: "budget-cashflow",
    title: "Budgeting & Cash Flow",
    pillar: "budget",
    description: "The 50/30/20 split, your month's cash-flow timeline, and sinking funds.",
    tools: [spendingPlanTile, cashFlowTile, sinkingFundTile],
  },
  {
    id: "home-purchases",
    title: "Home & Big Purchases",
    pillar: "protect",
    description: "What home you can afford, rent vs buy, and saving for college.",
    tools: [
      homeAffordabilityTile,
      rentVsBuyTile,
      pmiEndDateTile,
      homeSaleGainTile,
      collegeCostTile,
    ],
  },
  {
    id: "protection",
    title: "Insurance & Protection",
    pillar: "protect",
    description:
      "Health plan choice, life and disability cover, umbrella liability, and estate basics.",
    tools: [
      healthPlanTile,
      cobraVsMarketplaceTile,
      eobCheckerTile,
      lifeInsuranceTile,
      disabilityTile,
      umbrellaTile,
      estateChecklistTile,
    ],
  },
  {
    id: "benefits",
    title: "Benefits & Aid",
    pillar: "owed",
    description: "Benefits, credits, and aid you may be owed, screened in one place.",
    tools: [
      owedScreenerTile,
      fplTile,
      eitcTile,
      childTaxCreditTile,
      acaPtcTile,
      saversCreditTile,
      dependentCareTile,
      snapTile,
      medicaidTile,
      fafsaSaiTile,
      pellTile,
      educationCreditsTile,
    ],
  },
  {
    // Pillar 4, "Rough Water" (SPEC-4 §2.1, §7.1). Sits directly after Benefits
    // & Aid: a household that just screened what it's owed is exactly the one
    // that needs to know what happens to it when their income rises.
    id: "benefit-cliffs",
    title: "Benefit Cliffs",
    pillar: "rough",
    description: "Whether earning more actually leaves you better off, and where the edges are.",
    tools: [cliffExplorerTile, marginalRealityTile, secondIncomeTile],
  },
  {
    // Pillar 4 (SPEC-4 §7.1). Separate from "Borrowing & Debt" on purpose: that
    // hub is for planning a payoff, this one is for a month that doesn't close.
    id: "when-money-is-tight",
    title: "When Money Is Tight",
    pillar: "rough",
    description: "What to pay first when you can't pay everything, and what happens to the rest.",
    tools: [
      billTriageTile,
      lifeEventsTile,
      enrollmentWindowsTile,
      uiEstimatorTile,
      garnishmentTile,
      charityCareTile,
      freeFilingTile,
    ],
  },
  {
    id: "where-you-stand",
    title: "Where You Stand",
    pillar: "stand",
    description: "Your calm overview, your runway and net worth, and a sabbatical you can fund.",
    tools: [peaceOfMindTile, sabbaticalTile],
  },
];

export const TILES: TileDefinition[] = HUB_CONFIGS.map(defineHub);

/**
 * Every calculator hosted inside a hub, paired with its hub id. Used to emit a
 * crawlable SEO landing page per sub-tool (deep-linking into `?tool=`), so the
 * consolidation doesn't drop the 59 individual tool pages search engines index.
 */
export const SUB_TOOLS: { tile: TileDefinition; hubId: string }[] = HUB_CONFIGS.flatMap((h) =>
  h.tools.map((tile) => ({ tile, hubId: h.id })),
);

const BY_ID = new Map(TILES.map((t) => [t.id, t]));

export function getTile(id: string): TileDefinition | undefined {
  return BY_ID.get(id);
}

const HUB_BY_TOOL = new Map(SUB_TOOLS.map(({ tile, hubId }) => [tile.id, hubId]));

/**
 * The hub that owns a sub-tool, or undefined for a top-level route.
 *
 * A calculator is not a route: `federal-income-tax` is reached at
 * `#/paycheck-taxes?tool=federal-income-tax`, and `getTile` — which only knows
 * the routable tiles — returns nothing for it. So a `navigate("federal-income-tax")`
 * from anywhere outside that hub resolved to no tile and sent the reader home,
 * silently, which is how the Auto Loan tile's brand-new "Deduct this interest"
 * button behaved in a browser while its unit test passed.
 */
export function hubIdForTool(tileId: string): string | undefined {
  return HUB_BY_TOOL.get(tileId);
}

/**
 * A searchable entry for the home search and command palette. Hubs and every
 * sub-tool both appear, so searching "EITC"/"refinance" surfaces the familiar
 * calculator name and deep-links into its hub already switched to it.
 */
export interface SearchEntry {
  title: string;
  description: string;
  keywords: string[];
  /** Registry tile id to navigate to (a hub). */
  hubId: string;
  /** Sub-tool id to pre-select inside the hub (the `?tool=` value). */
  tool?: string;
}

/**
 * The text the fuzzy search matches for a {@link SearchEntry}: the title and the
 * curated keywords only — never the free-text description. Subsequence-matching
 * a long prose description produced confusing false positives (typing
 * "refinance" surfaced "Life Insurance Needs", "roth" surfaced "Home Buying
 * Readiness"), so the description is shown in the result but not searched, the
 * way a command palette matches a label and its tags rather than its blurb.
 */
export function searchEntryText(e: SearchEntry): string {
  return `${e.title} ${e.keywords.join(" ")}`;
}

export const SEARCH_ENTRIES: SearchEntry[] = [
  // Each hub as a whole (matches its topic words).
  ...HUB_CONFIGS.map((h) => ({
    title: h.title,
    description: h.description,
    keywords: [],
    hubId: h.id,
  })),
  // Every sub-tool, carrying its own name/keywords and a deep link into its hub.
  ...HUB_CONFIGS.flatMap((h) =>
    h.tools.map((t) => ({
      title: t.title,
      description: t.description,
      keywords: t.keywords,
      hubId: h.id,
      tool: t.id,
    })),
  ),
];
